import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';

import { useApiGet, useApiSend } from '../../../utils/httpClient';
import { formatAckAmount } from '../../../utils/formatters';
import { getApiErrorMessage } from '../../../utils/httpClientTs';
import {
  addDonationsToAckBatch,
  getAckBatchPickList,
  getAcknowledgementBatch,
  printAcknowledgementBatch,
  removeAckBatchItem,
} from '../../../endpoints/donations';

const PICK_PAGE_SIZE = 25;

export default function DonationAckBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [pickPage, setPickPage] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [errorMsg, setErrorMsg] = useState(null);
  const [printing, setPrinting] = useState(false);

  const { data, isLoading, refetch } = useApiGet(
    ['donation-ack-batch', batchId],
    () => getAcknowledgementBatch(batchId),
    { enabled: !!batchId, retry: 1 }
  );

  const batch = data?.batch;
  const items = data?.items ?? [];
  const pickEnabled = !!batchId && batch && !batch.sent_at;

  const { data: pickList, refetch: refetchPickList } = useApiGet(
    ['donation-ack-batch-pick', batchId, pickPage],
    () =>
      getAckBatchPickList({
        batchId,
        page: pickPage + 1,
        rowsPerPage: PICK_PAGE_SIZE,
      }),
    { enabled: pickEnabled, retry: 1 }
  );

  useEffect(() => {
    setPickPage(0);
    setSelected(new Set());
  }, [batchId]);

  const pendingRows = useMemo(() => pickList?.data ?? [], [pickList?.data]);
  const pickTotal = pickList?.pagination?.total_count ?? 0;
  const pageIds = useMemo(() => pendingRows.map((r) => r.id), [pendingRows]);

  const numSelectedOnPage = useMemo(
    () => pageIds.filter((id) => selected.has(id)).length,
    [pageIds, selected]
  );
  const allOnPageSelected =
    pageIds.length > 0 && numSelectedOnPage === pageIds.length;
  const someOnPageSelected =
    numSelectedOnPage > 0 && numSelectedOnPage < pageIds.length;

  const { mutate: addDonations, isPending: adding } = useApiSend(
    (donationIds) =>
      addDonationsToAckBatch({ batchId, donation_ids: donationIds }),
    () => {
      setErrorMsg(null);
      setSelected(new Set());
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: removeItem, isPending: removing } = useApiSend(
    (itemId) => removeAckBatchItem({ batchId, itemId }),
    () => {
      setErrorMsg(null);
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (checked) {
        pageIds.forEach((id) => n.add(id));
      } else {
        pageIds.forEach((id) => n.delete(id));
      }
      return n;
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = async () => {
    if (!batchId || !batch || batch.sent_at) return;
    if (items.length === 0) {
      setErrorMsg('Add at least one item to the batch before printing.');
      return;
    }
    setPrinting(true);
    setErrorMsg(null);
    try {
      const blob = await printAcknowledgementBatch(batchId);
      const filename = `ack-batch-${batch.batch_number || batchId}.pdf`;
      downloadBlob(blob, filename);
      await refetch();
      void refetchPickList();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err));
    } finally {
      setPrinting(false);
    }
  };

  const handleAddSelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      setErrorMsg('Select at least one donation using the checkboxes.');
      return;
    }
    addDonations(ids);
  };

  if (!batchId) {
    return <Typography>Missing batch id.</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/internal/donations')}
        sx={{ mb: 2 }}
      >
        Back to donations
      </Button>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      {isLoading && <Typography>Loading…</Typography>}

      {batch && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Acknowledgement batch {batch.batch_number}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {items.length} item(s) in batch
            {batch.sent_at ? ' · Sent' : ''}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              disabled={printing || !!batch.sent_at || items.length === 0}
              onClick={handlePrint}
            >
              {printing ? 'Printing…' : 'Print'}
            </Button>
          </Stack>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Pending acknowledgements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select donations to add to this batch. Each donation expands into
            acknowledgement items on the server.
          </Typography>

          <Paper sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ p: 2, flexWrap: 'wrap' }}
            >
              <Button
                variant="contained"
                disabled={adding || selected.size === 0 || !pickEnabled}
                onClick={handleAddSelected}
              >
                Add selected ({selected.size})
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someOnPageSelected}
                        checked={allOnPageSelected}
                        disabled={!pickEnabled}
                        onChange={(e) => toggleAllOnPage(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Donor</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>IHO / IMO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No pending donations available for this batch.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.has(row.id)}
                            disabled={!pickEnabled}
                            onChange={() => toggleOne(row.id)}
                          />
                        </TableCell>
                        <TableCell>{row.full_name}</TableCell>
                        <TableCell>{formatAckAmount(row.amount)}</TableCell>
                        <TableCell>{row.created_at}</TableCell>
                        <TableCell>{row.behalf_of_summary || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pickTotal}
              page={pickPage}
              onPageChange={(_, p) => {
                setPickPage(p);
                setSelected(new Set());
              }}
              rowsPerPage={PICK_PAGE_SIZE}
              rowsPerPageOptions={[PICK_PAGE_SIZE]}
            />
          </Paper>

          <Typography variant="h6" sx={{ mb: 1 }}>
            In this batch
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Donor</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Send</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No items yet. Add donations from the list above.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.donor_name || item.donation_id}
                      </TableCell>
                      <TableCell>{item.template_type}</TableCell>
                      <TableCell>{item.send_method}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          disabled={removing || !!batch.sent_at}
                          onClick={() => removeItem(item.id)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
